import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Download } from 'lucide-react';
import {
  BoutonPrincipal,
  BoutonSecondaire,
  Carte,
  Chargement,
  Compteur,
  EnteteEcran,
  MessageErreur,
  Pagination,
  Pastille,
  PastilleSite,
} from '../components/ui/Primitives';
import { ChampFiltre, ChampRecherche } from '../components/ui/Onglets';
import { BarreSites } from '../components/ui/BarreSites';
import { ModaleOrganisation } from '../components/repertoire/ModaleOrganisation';
import { useToast } from '../components/ui/useToast';
import { useAuth } from '../auth/useAuth';
import { usePerimetre } from '../site/usePerimetre';
import { TOUS_LES_SITES } from '../site/context';
import { useSites } from '../lib/useSites';
import { useTaillePage } from '../lib/useTaillePage';
import {
  useCreerPartenaire,
  useRepertoire,
  urlListeRepertoire,
  type LigneRepertoire,
} from '../lib/usePedagogique';
import { useCreerEntreprise } from '../lib/useEntreprises';
import { ErreurApi } from '../lib/api';
import { peutTenirLeRepertoire } from '../lib/droits-repertoire';

/**
 * Écran « Répertoire entreprises & partenaires » (CDC §6.10).
 *
 * « Liste générée par site des entreprises, partenaires et associations en lien
 * avec Top Formation. » Une seule liste réunit les entreprises du répertoire
 * commercial, les partenaires institutionnels, les prescripteurs et les
 * associations : c'est le référentiel commun que consultent la relation
 * entreprise, le recrutement, l'administratif et le pédagogique, chacun au
 * moment où son métier le demande.
 *
 * Nature, lien et compteurs viennent de l'API (`domain/repertoire.ts`) ; l'écran
 * s'ouvre sur ce que l'API retient par défaut, « en lien avec Top Formation »,
 * et n'en recopie pas la règle. « Générer la liste du site » imprime la liste
 * affichée en PDF, « Exporter en CSV » (au pied de la liste) la livre à un
 * tableur : mêmes filtres.
 *
 * Le nom et le bouton mènent au même endroit — le dossier de l'organisation,
 * `/ped-repertoire/:id`. Deux gestes, un seul comportement.
 *
 * **Tenir** le répertoire — y ajouter un contact — est en revanche une
 * responsabilité du pôle relation entreprise. Le bouton est absent pour les
 * autres, et l'API refuse l'appel : masquer le bouton seul ne serait pas une
 * garde.
 */
export function PedRepertoirePage(): JSX.Element {
  const toast = useToast();
  const { site } = usePerimetre();
  const { utilisateur } = useAuth();
  const [type, setType] = useState('');
  // Vide : l'API applique son défaut, et le rend dans `relation`.
  const [relation, setRelation] = useState('');
  const [recherche, setRecherche] = useState('');
  const [creation, setCreation] = useState(false);

  const filtres = {
    site,
    ...(type ? { type } : {}),
    ...(relation ? { relation } : {}),
    ...(recherche.trim() ? { recherche: recherche.trim() } : {}),
  };

  const { data, isLoading, error } = useRepertoire(filtres);
  const { data: tousLesSites } = useSites();
  const creerPartenaire = useCreerPartenaire();
  const creerEntreprise = useCreerEntreprise();

  const tientLeRepertoire = peutTenirLeRepertoire(utilisateur?.profil);

  const compteurs = data?.compteurs;
  const contacts = useMemo(() => data?.contacts || [], [data?.contacts]);

  const [page, setPage] = useState(1);
  const [taillePage, setTaillePage] = useTaillePage();

  useEffect(() => {
    setPage(1);
  }, [site, type, relation, recherche, taillePage]);

  const total = contacts.length;
  const pages = Math.max(1, Math.ceil(total / taillePage));
  const contactsAffiches = useMemo(() => {
    const debut = (page - 1) * taillePage;
    return contacts.slice(debut, debut + taillePage);
  }, [contacts, page, taillePage]);

  const generer = (format: 'pdf' | 'csv'): void => {
    if (total === 0) {
      toast('Aucun contact à exporter pour ce périmètre.');
      return;
    }
    window.location.href = urlListeRepertoire(filtres, format);
    toast(
      `${format === 'pdf' ? 'Liste générée' : 'Fichier exporté'} pour ${
        site === TOUS_LES_SITES ? 'tous les sites' : `l’antenne ${site}`
      } — ${total} contact${total > 1 ? 's' : ''}`,
    );
  };

  const enCours = creerPartenaire.isPending || creerEntreprise.isPending;
  const erreurCreation = creerPartenaire.error ?? creerEntreprise.error;

  const fermerCreation = (): void => {
    creerPartenaire.reset();
    creerEntreprise.reset();
    setCreation(false);
  };

  return (
    <div className="mx-auto max-w-[1500px] space-y-6 p-4 sm:p-6">
      <EnteteEcran
        titre="Répertoire entreprises & partenaires"
        sousTitre="Référentiel commun · entreprises, associations et partenaires, par antenne"
        actions={
          <div className="flex flex-wrap items-center gap-3">
            <BoutonSecondaire onClick={() => generer('pdf')}>
              Générer la liste du site
            </BoutonSecondaire>
            {tientLeRepertoire && (
              <BoutonPrincipal
                onClick={() => {
                  creerPartenaire.reset();
                  creerEntreprise.reset();
                  setCreation(true);
                }}
              >
                + Ajouter un contact
              </BoutonPrincipal>
            )}
          </div>
        }
      />

      <BarreSites />

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Compteur
          libelle="Entreprises"
          valeur={compteurs?.entreprises ?? '—'}
          detail="susceptibles d’accueillir"
          ton="navy"
        />
        <Compteur
          libelle="Partenaires"
          valeur={compteurs?.partenaires ?? '—'}
          detail="institutionnels et prescripteurs"
          ton="blue"
        />
        <Compteur
          libelle="Associations"
          valeur={compteurs?.associations ?? '—'}
          detail="accompagnement"
          ton="mint"
        />
        <Compteur
          libelle="Contacts"
          valeur={compteurs?.contacts ?? '—'}
          detail={site === TOUS_LES_SITES ? 'toutes antennes' : `antenne ${site}`}
          ton="warm"
        />
      </div>

      <Carte
        titre="Répertoire"
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <ChampRecherche
              valeur={recherche}
              onChange={setRecherche}
              etiquette="Rechercher une organisation"
              placeholder="Nom, activité, ville, SIRET, contact"
            />
            <ChampFiltre valeur={type} onChange={setType} etiquette="Filtrer par nature">
              <option value="">Toutes les natures</option>
              {(data?.types ?? []).map((entree) => (
                <option key={entree.valeur} value={entree.valeur}>
                  {entree.libelle}
                </option>
              ))}
            </ChampFiltre>
            <ChampFiltre
              valeur={relation || data?.relation || ''}
              onChange={setRelation}
              etiquette="Filtrer par relation avec Top Formation"
            >
              {(data?.relations ?? []).map((entree) => (
                <option key={entree.valeur} value={entree.valeur}>
                  {entree.libelle}
                </option>
              ))}
            </ChampFiltre>
          </div>
        }
        sansBordure
      >
        {error && (
          <div className="p-4 sm:p-6">
            <MessageErreur
              message={
                error instanceof ErreurApi
                  ? error.message
                  : 'Le répertoire n’a pas pu être chargé.'
              }
            />
          </div>
        )}
        {isLoading && <Chargement libelle="Chargement du répertoire…" />}

        {!isLoading && !error && total === 0 && (
          <p className="px-4 py-12 text-center text-sm text-codialis-gray sm:px-6">
            Aucune organisation ne correspond aux filtres actifs.
          </p>
        )}

        {/* Sous la tablette, une carte par organisation : le tableau y défilait
            de côté et le lien se lisait à l'aveugle. */}
        {!isLoading && !error && total > 0 && (
          <ul className="divide-y divide-gray-100 md:hidden">
            {contactsAffiches.map((contact) => (
              <li key={`${contact.type}-${contact.id}`} className="space-y-2 px-4 py-4">
                <div className="flex min-w-0 items-start justify-between gap-3">
                  <div className="min-w-0">
                    <Link
                      to={`/ped-repertoire/${contact.id}`}
                      className="font-semibold text-codialis-navy hover:text-codialis-sky hover:underline"
                    >
                      {contact.nom}
                    </Link>
                    <Activite contact={contact} />
                  </div>
                  <Pastille texte={contact.typeLibelle} ton={contact.typeTon} />
                </div>
                <Lien contact={contact} />
                <Sites contact={contact} />
                <Coordonnees contact={contact} />
                <Link
                  to={`/ped-repertoire/${contact.id}`}
                  className="inline-flex min-h-[44px] items-center text-xs font-semibold text-codialis-sky hover:underline"
                >
                  Ouvrir le dossier
                </Link>
              </li>
            ))}
          </ul>
        )}

        {!isLoading && !error && total > 0 && (
          <div className="relative hidden overflow-x-auto md:block">
            <table className="w-full min-w-[640px] text-sm">
              <thead className="border-b border-gray-100 bg-gray-50">
                <tr>
                  {['Organisation', 'Nature · lien avec Top Formation', 'Contact', ''].map(
                    (colonne, index) => (
                      <th
                        key={colonne || index}
                        className="px-3 py-3 text-left text-[11px] font-bold uppercase tracking-wider text-codialis-gray lg:px-4"
                      >
                        {colonne || <span className="sr-only">Actions</span>}
                      </th>
                    ),
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {contactsAffiches.map((contact) => (
                  <tr key={`${contact.type}-${contact.id}`} className="align-top transition hover:bg-gray-50">
                    <td className="px-3 py-3 lg:px-4">
                      {/* Le nom mène au dossier, comme le bouton de droite. */}
                      <Link
                        to={`/ped-repertoire/${contact.id}`}
                        className="font-semibold text-codialis-navy hover:text-codialis-sky hover:underline"
                      >
                        {contact.nom}
                      </Link>
                      <Activite contact={contact} />
                      <div className="mt-1.5">
                        <Sites contact={contact} />
                      </div>
                    </td>
                    <td className="space-y-1.5 px-3 py-3 lg:px-4">
                      <Pastille texte={contact.typeLibelle} ton={contact.typeTon} />
                      <Lien contact={contact} />
                    </td>
                    <td className="px-3 py-3 lg:px-4">
                      <Coordonnees contact={contact} />
                    </td>
                    <td className="px-3 py-3 text-right lg:px-4">
                      <Link
                        to={`/ped-repertoire/${contact.id}`}
                        className="whitespace-nowrap text-xs font-semibold text-codialis-sky hover:underline"
                      >
                        Ouvrir le dossier
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {!isLoading && !error && total > 0 && (
          <Pagination
            page={page}
            pages={pages}
            total={total}
            affiches={contactsAffiches.length}
            taillePage={taillePage}
            onPage={setPage}
            onChangerTaille={setTaillePage}
            uniteLabel="contact"
          />
        )}
        <div className="space-y-1 border-t border-gray-100 p-4 text-[11px] text-codialis-gray sm:p-6">
          {/* Le fichier pour un tableur est un geste second : la liste à
              imprimer, en tête d'écran, est celle que le CDC demande. */}
          <button
            type="button"
            onClick={() => generer('csv')}
            className="inline-flex min-h-[32px] items-center gap-1.5 text-xs font-semibold text-codialis-sky hover:underline [@media(pointer:coarse)]:min-h-[44px]"
          >
            <Download className="h-3.5 w-3.5" aria-hidden />
            Exporter en CSV
          </button>
          <p>
            En lien avec Top Formation : les partenaires et associations du répertoire, et les
            entreprises partenaires, ou qui accueillent un apprenti, préparent un contrat ou
            reçoivent une immersion. Prospects et relations inactives se retrouvent par le filtre
            de relation.
          </p>
          <p>
            Une entreprise dont la relation est institutionnelle apparaît comme partenaire
            institutionnel, pas comme entreprise d’accueil : elle n’accueille pas d’apprenti.
          </p>
        </div>
      </Carte>

      {/* La fiche est la même pour les trois natures : seule la nature choisie
          décide de ce qui s'ajoute — SIRET et données d'employeur, ou RNA. */}
      {creation && (
        <ModaleOrganisation
          ouverte
          statutsRelation={(data?.statutsRelation ?? []).map((statut) => ({
            valeur: statut.valeur,
            libelle: statut.libelle,
          }))}
          typesPartenaire={(data?.types ?? []).filter((entree) => entree.valeur !== 'ENTREPRISE')}
          secteurs={data?.secteursSuggeres ?? []}
          typesEmployeur={data?.typesEmployeurSuggeres ?? []}
          employeursSpecifiques={data?.employeursSpecifiquesSuggeres ?? []}
          sitesDisponibles={(tousLesSites?.sites ?? []).map((entree) => entree.code)}
          sitesParDefaut={site === TOUS_LES_SITES ? [] : [site]}
          enCours={enCours}
          erreur={erreurCreation instanceof ErreurApi ? erreurCreation.message : null}
          onFermer={fermerCreation}
          onEnregistrer={(sortie) => {
            const fini = {
              onSuccess: () => {
                toast('Contact ajouté au répertoire');
                fermerCreation();
              },
            };
            if (sortie.genre === 'ENTREPRISE' && sortie.entreprise) {
              // `effectif` non saisi arrive à `null` — utile à la correction
              // pour effacer, refusé à la création : on l'omet.
              const { effectif, ...entree } = sortie.entreprise;
              creerEntreprise.mutate(
                { ...entree, ...(effectif === null ? {} : { effectif }) },
                fini,
              );
            } else if (sortie.partenaire) {
              creerPartenaire.mutate(sortie.partenaire, fini);
            }
          }}
        />
      )}
    </div>
  );
}

/** Identifiant, activité et ville, sous le nom de l'organisation. */
function Activite({ contact }: { contact: LigneRepertoire }): JSX.Element {
  const activite = [contact.domaine, contact.ville].filter(Boolean).join(' · ');
  return (
    <>
      {contact.identifiant && (
        <div className="whitespace-nowrap font-mono text-[10px] text-codialis-gray">
          {contact.identifiant}
        </div>
      )}
      {activite && <div className="text-xs text-codialis-gray">{activite}</div>}
    </>
  );
}

function Lien({ contact }: { contact: LigneRepertoire }): JSX.Element {
  return (
    <div className="space-y-1">
      <Pastille texte={contact.lien.libelle} ton={contact.lien.ton} />
      {contact.lien.details.map((detail) => (
        <div key={detail} className="text-xs text-codialis-gray">
          {detail}
        </div>
      ))}
    </div>
  );
}

function Sites({ contact }: { contact: LigneRepertoire }): JSX.Element {
  if (contact.toutesAntennes) return <Pastille texte="Toutes antennes" ton="neutral" />;
  return (
    <div className="flex flex-wrap gap-1">
      {contact.sites.map((code) => (
        <PastilleSite key={code} site={code} />
      ))}
    </div>
  );
}

/**
 * Une adresse e-mail se coupe d'abord avant l'arobase : coupée n'importe où, la
 * colonne étroite de la tablette laissait « entreprise.tes » d'un côté et « t »
 * de l'autre.
 */
function Courriel({ adresse }: { adresse: string }): JSX.Element {
  const arobase = adresse.indexOf('@');
  if (arobase <= 0) return <>{adresse}</>;
  return (
    <>
      {adresse.slice(0, arobase)}
      <wbr />
      {adresse.slice(arobase)}
    </>
  );
}

function Coordonnees({ contact }: { contact: LigneRepertoire }): JSX.Element {
  const lignes = [contact.contact, contact.telephone].filter(
    (valeur): valeur is string => Boolean(valeur),
  );
  if (lignes.length === 0 && !contact.email) {
    return <span className="text-xs text-codialis-gray">—</span>;
  }
  return (
    <div className="min-w-0 space-y-0.5 text-xs text-codialis-gray">
      {lignes.map((ligne) => (
        <div key={ligne} className="[overflow-wrap:anywhere]">
          {ligne}
        </div>
      ))}
      {contact.email && (
        <div className="[overflow-wrap:anywhere]">
          <Courriel adresse={contact.email} />
        </div>
      )}
    </div>
  );
}
